import { StockStatusEnum } from '@petsmart-ui/sparky';

import type { ProductHit } from '@libs/algolia';
import { getDefaultCategories } from '@libs/algolia';
import type { ItemsListItem } from '@libs/common-next';
import { formatAutoshipFrequency, NavigationListTypes } from '@libs/common-next';
import type { CartUI, ItemUI } from '@libs/common-node';
import { joinUrlOrPath } from '@libs/core';

import { TrackingParams } from '../constants';
import { buildFulfillmentMethodLabel } from '../helpers/buildFulfillmentMethodLabel';
import { resolveGa4ItemListAttributionForCartItem } from '../helpers/resolveGa4ItemListAttributionForCartItem';
import { formatScheduledDeliveryDate } from '../helpers/scheduledDeliveryDateUtils';
import { getSkuSearchTerm, resolveItemSearchTerm } from '../helpers/skuSearchTermStorage';
import { getSearchStats } from '../helpers/storageSearch';
import { getGa4ItemId } from './getGa4ItemId';

export {
    type Ga4ItemListAttribution,
    getGa4ItemListAttributionFromSkuFindingMethod,
    getNavigationId,
    getNavigationName,
    processFindingMethod,
} from '../helpers/ga4ItemListAttributionFromSkuFindingMethod';

export const getProductObject = (product: ProductHit) => ({
    name: product.name,
    id: product.objectID,
    brand: product.brand,
    dimension5: product['pharmacy-type'] || 'Other',
    dimension18: product.color?.value,
    dimension19: product.size ? Object.values(product.size)[0] : undefined,
    dimension20: product.flavor?.value,
    dimension21: product.customCaseQuantity,
    dimension41: product.merchandisingHierarchy?.vicePresidentName,
    dimension42: product.merchandisingHierarchy?.division,
    dimension43: product.merchandisingHierarchy?.department,
    dimension44: product.merchandisingHierarchy?.class,
    dimension45: product.merchandisingHierarchy?.category,
});

export const mapProductClick = (product: ProductHit) => {
    const price = product.price?.number || (product.prices ? Number(product.prices[0]) : undefined);
    return {
        name: product.name || product.title,
        id: product.objectID,
        brand: product.brand,
        price,
        dimension5: product['pharmacy-type'] || 'Other',
        dimension6: TrackingParams.STANDARD_PDP,
        dimension41: product.merchandisingHierarchy?.vicePresidentName,
        dimension42: product.merchandisingHierarchy?.division,
        dimension43: product.merchandisingHierarchy?.department,
        dimension44: product.merchandisingHierarchy?.class,
        dimension45: product.merchandisingHierarchy?.category,
    };
};

export const getAvailabilityConstant = (ats: number) => (ats > 0 ? TrackingParams.YES : TrackingParams.NO);

export const getStockLevelConstant = (stockLevel: StockStatusEnum) =>
    ({
        [StockStatusEnum.InStock]: 'In Stock',
        [StockStatusEnum.FewLeftInStock]: 'Low Stock',
        [StockStatusEnum.OutOfStock]: 'Out of Stock',
        [StockStatusEnum.NotAvailable]: null,
    })[stockLevel];

export const getStockAvailabilityFields = (
    id: string,
    enrichedItemsList: ItemsListItem[]
): Record<string, string | boolean> => {
    const enriched = enrichedItemsList.find(i => i.id === id);
    if (!enriched) return {};
    return {
        sthStockAvailability: enriched.sthStockAvailability ?? '',
        bopisStockAvailability: enriched.bopisStockAvailability ?? '',
        sddStockAvailability: enriched.sddStockAvailability ?? '',
        sth_availability: enriched.sth_availability ?? false,
        bopis_availability: enriched.bopis_availability ?? false,
        sdd_availability: enriched.sdd_availability ?? false,
    };
};

export const getDimensionCategories = (product: ProductHit) => {
    const categories = getDefaultCategories(product);
    return categories?.length
        ? {
              dimension9: categories[0]?.name.default ?? null,
              dimension10: categories[1]?.name.default ?? null,
              dimension11: categories[2]?.name.default ?? null,
          }
        : {};
};

export const extractCategoriesFromUrl = (
    productUrl: string | undefined
): { category: string | null; subCategory: string | null } => {
    if (!productUrl) {
        return { category: null, subCategory: null };
    }

    try {
        let pathOnly = productUrl;
        try {
            const url = new URL(productUrl);
            pathOnly = url.pathname;
        } catch {}

        const cleanUrl = pathOnly
            .replace(/^\/+|\/+$/g, '')
            .split('?')[0]
            .replace(/\.html?$/, '');

        const urlParts = cleanUrl.split('/').filter(Boolean);

        return {
            category: urlParts[0] ?? null,
            subCategory: urlParts[1] ?? null,
        };
    } catch {
        return { category: null, subCategory: null };
    }
};

export const mapImpressions = (products: ProductHit[], list: string) =>
    products.map(product => ({
        list,
        price: product.price?.number,
        position: product.__position,
        ...getDimensionCategories(product),
        ...getProductObject(product),
    }));

export const getBaseUrlJoined = (path: string | undefined) =>
    path ? joinUrlOrPath(window.location.origin, path) : path;

export const getSubmitSearchType = (searchAssistName?: string) =>
    searchAssistName ? 'assisted search' : 'manual search';

export const getViewSearchResultsType = (numberOfResults: number, searchType?: string) =>
    numberOfResults === 0 ? 'no results' : searchType === 'manual' ? 'manual search' : 'assisted search';

export const getPromoOrigin = (pathname?: string | null) =>
    pathname !== '/' ? pathname?.substring(1).replace(/\//g, '-') : 'homepage';

export const getPromoDesc = (desc?: string) => desc?.toLowerCase().replace(/\s+/g, '-') ?? '';

export const mapCategoriesFromUrl = (pathname: string | null) => pathname?.split('/').filter(item => item !== '') || [];

export const mapCartItem = (item: ItemsListItem) => ({
    name: item.name,
    id: item.sku,
    price: item.totalPrice / 100,
    brand: item.merchandiseBrand,
    quantity: item.quantity,
    dimension18: item.variant?.color ?? null, // Color of the item; null if not applicable
    dimension19: item.variant?.size ?? null,
    dimension20: item.variant?.flavor ?? null, // Flavor of the item; null if not applicable
    dimension5: item.pharmacyType ?? 'Others', // Product type, options include Others, Vet Diet, Pharmacy
    dimension21: item.customCaseQuantity ?? null,
    dimension12: item.fulfillmentMethod?.toLowerCase() ?? null,
    dimension14: item.storeId ?? null,
    dimension13: item.autoshipFrequency ?? null,
    dimension39: 'Yes',
    dimension41: item.mhVicePresidentName,
    dimension42: item.mhDivision,
    dimension43: item.mhDepartment,
    dimension44: item.mhClass,
    dimension45: item.mhCategory,
    metric4: item.basePrice / 100,
    metric5: item.salePrice / 100,
    maxQtyReached: item.quantity < item.availableQuantity ? 'no' : 'yes',
    dimension31: item.positionWithinCart,
});

export const mapItem = (item: ItemUI) => {
    const listAttribution = resolveGa4ItemListAttributionForCartItem(item);

    return {
        name: item.name,
        id: item.sku,
        price: item.totalPrice,
        brand: item.merchandiseBrand,
        quantity: item.quantity,
        dimension18: item.variant?.color ?? null, // Color of the item; null if not applicable
        dimension19: item.variant?.size ?? null,
        dimension20: item.variant?.flavor ?? null, // Flavor of the item; null if not applicable
        dimension5: item.pharmacyType ?? 'Others', // Product type, options include Others, Vet Diet, Pharmacy
        dimension21: item.customCaseQuantity ?? null,
        dimension12: null,
        dimension14: null,
        dimension13: item.autoshipFrequency ?? null,
        dimension39: 'Yes',
        dimension41: item.mhVicePresidentName,
        dimension42: item.mhDivision,
        dimension43: item.mhDepartment,
        dimension44: item.mhClass,
        dimension45: item.mhCategory,
        metric4: item.basePrice,
        metric5: item.salePrice,
        maxQtyReached: item.quantity < item.availableQuantity ? 'no' : 'yes',
        dimension31: item.positionWithinCart,
        item_list_name: listAttribution.item_list_name,
        item_list_id: listAttribution.item_list_id,
        ...(listAttribution.index !== undefined && { index: listAttribution.index }),
    };
};

export const mapCheckoutItems = (cart: CartUI, isCrawl?: boolean) => {
    return cart.fulfillmentGroups.flatMap(group =>
        group.items.map(item => {
            const listAttribution = resolveGa4ItemListAttributionForCartItem(item);

            return {
                item_name: item.name,
                item_id: getGa4ItemId(item),
                quantity: item.quantity,
                price: item.salePrice,
                item_brand: item.merchandiseBrand,
                fulfillment_method: buildFulfillmentMethodLabel(group.fulfillmentMethod, item),
                autoship_frequency: formatAutoshipFrequency(
                    item.autoshipFrequency,
                    item.autoshipFrequencyUnitOfMeasure
                ),
                store_id: group.storeId,
                size: item.variant?.size,
                flavor: item.variant?.flavor,
                color: item.variant?.color,
                item_cart_order: item.positionWithinCart,
                item_category: item.mhVicePresidentName,
                item_category2: item.mhDivision,
                item_category3: item.mhDepartment,
                item_category4: item.mhClass,
                item_category5: item.mhCategory,
                item_variant: item.sku,
                default_version: item.masterProductSku === item.sku ? 'yes' : 'no',
                item_list_name: listAttribution.item_list_name,
                item_list_id: listAttribution.item_list_id,
                ...(listAttribution.index !== undefined && { index: listAttribution.index }),
                item_search_term: resolveItemSearchTerm(
                    getSkuSearchTerm(item.sku),
                    listAttribution.item_list_id === NavigationListTypes.SEARCH
                        ? (getSearchStats().searchTerm ?? null)
                        : null
                ),
                ...(isCrawl && {
                    sth_availability: item?.sth_availability ?? '',
                    bopis_availability: item?.bopis_availability ?? '',
                    sdd_availability: item?.sdd_availability ?? '',
                    sth_stock_availability: item?.sthStockAvailability ?? '',
                    bopis_stock_availability: item?.bopisStockAvailability ?? '',
                    sdd_stock_availability: item?.sddStockAvailability ?? '',
                }),
                scheduled_autoship_date: formatScheduledDeliveryDate(item.scheduledDeliveryDate),
            };
        })
    );
};

export const getRoughTotal = (items: ReadonlyArray<{ price?: number }>): number =>
    items.reduce<number>((acc, item) => acc + (item.price ?? 0), 0);

export const roundTotal = (amount: number): number => Math.round(amount * 100) / 100;

export const toDollarsAmount = (amount: number): number => amount / 100;

export const getItemDiscount = (item: ItemUI) => item.basePrice * item.quantity - (item.salePrice ?? 0);
